/**
 * Merci : la rangée de catégories des pages de collection, à la souris.
 *
 * Une famille trop longue défile horizontalement et s'efface en fondu (choix
 * de Guillaume, le 2026-10-08 — voir `styles/_collection-toolbar.scss`). Au
 * doigt et au pavé tactile, le défilement horizontal est natif ; à la souris,
 * la molette ne fait défiler que verticalement, et la rangée restait
 * immobile — relevé par Guillaume. Deux gestes la rendent utilisable :
 *
 *   - **la molette** fait défiler la rangée tant qu'elle a de la course dans
 *     ce sens, puis rend la main à la page : arrivé au bout, on continue de
 *     descendre normalement ;
 *   - **le glisser** à la souris fait défiler la rangée ; le clic qui le termine
 *     est avalé, sinon lâcher sur un lien l'ouvrirait.
 *
 * Écouteurs **délégués au document** : le bloc de filtres est re-rendu quand
 * on filtre, et la rangée avec lui.
 */

const SELECTOR = '.merci-collection-links';

/** Distance, en pixels, au-delà de laquelle un appui devient un glisser. */
const DRAG_THRESHOLD = 5;

/** @param {EventTarget | null} target */
function rowOf(target) {
  return target instanceof Element ? target.closest(SELECTOR) : null;
}

/** @param {Element} row */
function canScroll(row) {
  return row.scrollWidth > row.clientWidth;
}

// --- La molette ---------------------------------------------------------------

document.addEventListener(
  'wheel',
  (event) => {
    const row = rowOf(event.target);
    if (!row || !canScroll(row)) return;

    // Un geste déjà horizontal (pavé tactile, Maj + molette) : natif.
    if (Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return;

    const max = row.scrollWidth - row.clientWidth;
    const atStart = row.scrollLeft <= 0;
    const atEnd = row.scrollLeft >= max - 1;
    if ((event.deltaY < 0 && atStart) || (event.deltaY > 0 && atEnd)) return;

    event.preventDefault();
    row.scrollLeft += event.deltaY;
  },
  { passive: false }
);

// --- Le glisser ------------------------------------------------------------------

/** @type {{ row: Element, x: number, scrollLeft: number, dragging: boolean } | null} */
let drag = null;
let swallowClick = false;

document.addEventListener('pointerdown', (event) => {
  if (event.pointerType !== 'mouse' || event.button !== 0) return;
  const row = rowOf(event.target);
  if (!row || !canScroll(row)) return;
  drag = { row, x: event.clientX, scrollLeft: row.scrollLeft, dragging: false };
});

document.addEventListener('pointermove', (event) => {
  if (!drag) return;
  const dx = event.clientX - drag.x;
  if (!drag.dragging && Math.abs(dx) < DRAG_THRESHOLD) return;

  drag.dragging = true;
  drag.row.classList.add('is-dragging');
  drag.row.scrollLeft = drag.scrollLeft - dx;
});

document.addEventListener('pointerup', () => {
  if (!drag) return;
  if (drag.dragging) {
    swallowClick = true;
    setTimeout(() => (swallowClick = false), 0);
  }
  drag.row.classList.remove('is-dragging');
  drag = null;
});

document.addEventListener(
  'click',
  (event) => {
    if (!swallowClick || !rowOf(event.target)) return;
    event.preventDefault();
    event.stopPropagation();
    swallowClick = false;
  },
  { capture: true }
);

// Un lien se laisse glisser comme une image par défaut : le navigateur
// lancerait son propre glisser-déposer à la place du nôtre.
document.addEventListener('dragstart', (event) => {
  if (rowOf(event.target)) event.preventDefault();
});

// --- L'amorce ---------------------------------------------------------------------
//
// Demande de la cliente, relayée par Guillaume le 2026-10-14 : le site actuel
// signalait qu'une rangée défile en la faisant glisser un instant vers la
// gauche puis revenir — l'animation `rebond` de son `_collection.scss` : 100 px,
// une seconde, deux secondes après l'affichage, une fois.
//
// Reprise avec trois écarts :
//
//   - **seulement si la rangée déborde** — le site actuel la jouait toujours,
//     en mobile, qu'il y ait quelque chose à voir ou non ;
//   - **un vrai défilement** plutôt qu'un déplacement du contenu : le fondu des
//     bords suit le défilement (`_collection-toolbar.scss`), il accompagne donc
//     le geste au lieu de rester figé pendant que les liens passent dessous ;
//   - **elle cède la main** : un appui, une molette, un toucher sur la rangée
//     l'arrêtent, et la préférence « réduire les animations » l'écarte.
//
// Une fois par chargement de page : la rangée est re-rendue à chaque filtre, et
// rejouer l'amorce à chaque clic deviendrait une gêne.

/** Amplitude, délai et durée de l'amorce — ceux du site actuel. */
const HINT_DISTANCE = 100;
const HINT_DELAY = 2000;
const HINT_DURATION = 1000;

/** Part de la durée passée à aller, le reste au retour (le 40 % du site actuel). */
const HINT_OUT = 0.4;

let hintPlayed = false;

/** @param {number} t */
const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2);

/** @param {Element} row */
function playHint(row) {
  if (hintPlayed || !canScroll(row)) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  hintPlayed = true;

  // Vers la suite de la rangée s'il en reste assez, sinon vers le début — la
  // catégorie courante peut l'avoir déjà poussée au bout (`blocks/filters.liquid`).
  const start = row.scrollLeft;
  const toEnd = row.scrollWidth - row.clientWidth - start;
  const distance = toEnd >= 24 ? Math.min(HINT_DISTANCE, toEnd) : -Math.min(HINT_DISTANCE, start);
  if (Math.abs(distance) < 24) return;

  let stopped = false;
  const stop = () => (stopped = true);
  const gestures = ['pointerdown', 'wheel', 'touchstart'];
  gestures.forEach((type) => row.addEventListener(type, stop, { once: true, passive: true }));

  const t0 = performance.now();
  const step = (now) => {
    if (stopped || !row.isConnected) return;
    const p = Math.min((now - t0) / HINT_DURATION, 1);
    const offset = p < HINT_OUT ? easeInOut(p / HINT_OUT) : easeInOut(1 - (p - HINT_OUT) / (1 - HINT_OUT));
    row.scrollLeft = start + distance * offset;
    if (p < 1) requestAnimationFrame(step);
    else gestures.forEach((type) => row.removeEventListener(type, stop));
  };

  requestAnimationFrame(step);
}

// Le délai court à partir du moment où la rangée est **à l'écran**, pas du
// chargement : arrivée en bas de page par un retour arrière, elle n'aurait rien
// montré à personne.
const firstRow = document.querySelector(SELECTOR);
if (firstRow && 'IntersectionObserver' in window) {
  const observer = new IntersectionObserver((entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    observer.disconnect();
    setTimeout(() => {
      const row = document.querySelector(SELECTOR);
      if (row) playHint(row);
    }, HINT_DELAY);
  });
  observer.observe(firstRow);
}
